import {
  Box,
  CopyToClipboard,
  ExpandableSection,
  Icon,
  SpaceBetween,
} from "@amzn/awsui-components-console";
import React, { useEffect, useMemo, useState } from "react";

import LoadingSpinner from "@/components/LoadingSpinner";
import {
  GroupedStep,
  groupSteps,
} from "@/components/processing-steps/groupSteps";
import MetricsSection from "@/components/processing-steps/MetricsSection";
import { consolidateSteps } from "@/components/processing-steps/utils";
import { useChat } from "@/contexts/ChatContext";
import { ProcessingStepSection } from "@/types/chat-response";

/** Strip emoji characters from text. */
const stripEmoji = (text: string): string =>
  text
    .replace(
      /[\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{27BF}\u{2B50}\u{2B55}\u{231A}-\u{23F3}\u{23F8}-\u{23FA}\u{25AA}-\u{25FE}\u{2934}-\u{2935}\u{2190}-\u{21FF}]/gu,
      ""
    )
    // Variation selector, ZWJ, and keycap combining marks are stripped via
    // alternation (not a character class) so they don't form misleading
    // base+combining sequences.
    .replace(/\u{FE0F}|\u{200D}|\u{20E3}/gu, "")
    .replace(/^\s+/, "");

/** Get a short label for a step. */
const getStepLabel = (section: string, content: string): string => {
  const clean = stripEmoji(content);

  if (
    section === ProcessingStepSection.AGENT_PROGRESS ||
    section === ProcessingStepSection.AGENT_PROCESSING
  ) {
    if (!clean || clean.length === 0) return section;
    const preview = clean.length > 40 ? clean.slice(0, 40) + "..." : clean;
    return preview;
  }

  // For tool sections, the section name IS the label (e.g., "Tool: Querying NBA database")
  if (section.startsWith("Tool:")) {
    return section;
  }

  return section;
};

/** Format elapsed seconds. */
const formatElapsed = (seconds: number): string => {
  if (seconds < 1) return "<1s";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

/** Compute elapsed time for a group (from first step to next group start or now). */
const computeGroupElapsed = (
  groups: GroupedStep[],
  index: number,
  now: number
): number => {
  const group = groups[index];
  const groupStart = new Date(group.step.timestamp).getTime();

  if (index === 0) {
    // Most recent group (newest-first) — elapsed = now - start
    return (now - groupStart) / 1000;
  }

  // Elapsed = previous group's start (which is newer) - this group's start
  const newerGroupStart = new Date(groups[index - 1].step.timestamp).getTime();
  return (newerGroupStart - groupStart) / 1000;
};

const ProcessingStepsPanel: React.FC = () => {
  const [now, setNow] = useState(Date.now());
  const { processingSteps, chatLoading } = useChat();

  // Tick every second while chat is loading
  useEffect(() => {
    if (!chatLoading) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [chatLoading]);

  useEffect(() => {
    setNow(Date.now());
  }, [processingSteps.length]);

  // Group steps into hierarchy, then reverse so newest is at the top.
  // Filter out sections that are noise for the user (Metrics rendered
  // separately; Safety/Tracing just clutter the view).
  const HIDDEN_SECTIONS = new Set<string>([
    ProcessingStepSection.METRICS,
    ProcessingStepSection.SAFETY_CHECK,
    ProcessingStepSection.RESPONSE_SAFETY,
    ProcessingStepSection.TRACING,
    ProcessingStepSection.TRACE_DETAILS,
    "Execution Summary",
  ]);

  const groups = useMemo(
    () =>
      groupSteps(
        processingSteps.filter(
          (s) => !HIDDEN_SECTIONS.has(s.section as ProcessingStepSection)
        )
      ).reverse(),
    [processingSteps]
  );

  // Extract metrics separately
  const { metrics } = processingSteps.reduce(consolidateSteps, {
    steps: [],
    metrics: undefined,
  });

  /** Format all processing steps as plain text for clipboard copy. */
  const stepsAsText = useMemo(() => {
    if (processingSteps.length === 0) return "";
    return processingSteps
      .map((step) => {
        const content =
          typeof step.content === "string" ? step.content : "";
        return `[${step.section}] ${content}`;
      })
      .join("\n");
  }, [processingSteps]);

  return (
    <Box padding={{ horizontal: "l", top: "s", bottom: "l" }}>
      <SpaceBetween size="xxs">
        {chatLoading && <LoadingSpinner size="normal" />}
        {metrics && <MetricsSection metrics={metrics} />}
      {groups.length > 0 ? (
        <>
          {groups.map((group, index) => {
            const section = group.step.section as string;
            const content =
              typeof group.step.content === "string"
                ? group.step.content
                : "";
            const label = getStepLabel(section, content);
            const elapsed = computeGroupElapsed(groups, index, now);
            const isActive = index === 0 && chatLoading;
            const hasChildren = group.children.length > 0;

            // Determine icon
            const isAgent =
              section === ProcessingStepSection.AGENT_PROGRESS ||
              section === ProcessingStepSection.AGENT_PROCESSING;
            const isTool = section.startsWith("Tool:");
            const iconName = isAgent
              ? "contact"
              : isTool
              ? "script"
              : section === ProcessingStepSection.SAFETY_CHECK ||
                section === ProcessingStepSection.RESPONSE_SAFETY
              ? "security"
              : section === ProcessingStepSection.FINAL_RESPONSE
              ? "status-positive"
              : "status-info";

            // Header row
            const header = (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "flex-start",
                  gap: "8px",
                  width: "100%",
                  // While loading, dim completed steps to highlight the active one.
                  // Once done, show all at full opacity.
                  opacity: chatLoading && !isActive ? 0.5 : 1,
                  transition: "opacity 0.3s ease",
                }}
              >
                <Icon name={iconName} size="small" />
                <span
                  style={{
                    whiteSpace: "normal",
                    overflow: "hidden",
                    minWidth: 0,
                    flex: 1,
                  }}
                >
                  {label}
                </span>
                <span
                  style={{
                    marginLeft: "auto",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    color="text-status-inactive"
                    display="inline"
                    fontSize="body-s"
                  >
                    {formatElapsed(elapsed)}
                  </Box>
                </span>
              </span>
            );

            // If no children and no expandable content, render flat
            if (!hasChildren && !content) {
              return (
                <Box
                  key={`group-${index}`}
                  padding={{ vertical: "xxxs", horizontal: "xs" }}
                >
                  {header}
                </Box>
              );
            }

            // Render as expandable with children
            return (
              <ExpandableSection
                key={`group-${index}`}
                variant="footer"
                headerText={header}
                defaultExpanded={isActive}
              >
                {/* Parent's own content (agent thinking text) */}
                {content && (
                  <Box
                    padding={{ left: "s" }}
                    margin={{ bottom: "xxs" }}
                    color="text-body-secondary"
                    fontSize="body-s"
                  >
                    {stripEmoji(content)}
                  </Box>
                )}

                {/* Child steps (tools) */}
                {hasChildren && (
                  <SpaceBetween size="xxxs">
                    {group.children.map((child, childIdx) => {
                      const childSection = child.section as string;
                      const childLabel = childSection.startsWith("Tool:")
                        ? childSection.replace("Tool: ", "")
                        : childSection;
                      const childIcon = childSection.startsWith("Tool:")
                        ? "script"
                        : "status-info";

                      return (
                        <Box
                          key={`child-${index}-${childIdx}`}
                          padding={{ left: "l" }}
                        >
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                          >
                            <Icon
                              name={childIcon}
                              size="small"
                              variant="subtle"
                            />
                            <Box
                              color="text-body-secondary"
                              fontSize="body-s"
                              display="inline"
                            >
                              {childLabel}
                            </Box>
                          </span>
                        </Box>
                      );
                    })}
                  </SpaceBetween>
                )}
              </ExpandableSection>
            );
          })}

          {/* Copy all steps button */}
          {!chatLoading && processingSteps.length > 0 && (
            <Box padding={{ top: "xs" }}>
              <CopyToClipboard
                copyButtonText="Copy all steps"
                copyButtonAriaLabel="Copy all processing steps"
                copyErrorText="Failed to copy"
                copySuccessText="Copied steps"
                textToCopy={stepsAsText}
                variant="button"
              />
            </Box>
          )}
        </>
      ) : (
        !chatLoading && (
          <Box color="text-status-inactive">
            No steps are currently available. Send a message to see processing
            details here.
          </Box>
        )
      )}
      </SpaceBetween>
    </Box>
  );
};

export default ProcessingStepsPanel;
